/**
 * Admin LoveLink Earnings Panel
 * Manage community payouts with Net-30 enforcement
 */
import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../../App';
import { Loader, DollarSign, Filter, CheckCircle2, AlertTriangle } from 'lucide-react';

type AdminEarning = {
  id: string;
  inviter_id: string;
  inviter_email?: string;
  invitee_id: string;
  invitee_email?: string;
  product_label: string;
  amount_total_cents: number;
  commission_amount_cents: number;
  currency: string;
  status: 'pending' | 'paid';
  created_at: string;
  is_mature: boolean;
  days_until_mature?: number | null;
  payout_reference?: string | null;
};

type AdminEarningsResponse = {
  total_pending_cents: number;
  earnings: AdminEarning[];
};

const centsToDollars = (cents: number | undefined | null): string => {
  if (!cents) return '$0.00';
  return `$${(cents / 100).toFixed(2)}`;
};

const AdminLoveLinkEarnings: React.FC = () => {
  const { token, user } = useContext(AuthContext);
  const [statusFilter, setStatusFilter] = useState<'pending' | 'paid' | ''>('pending');
  const [data, setData] = useState<AdminEarningsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [markingId, setMarkingId] = useState<string | null>(null);
  const [payoutNote, setPayoutNote] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);

  useEffect(() => {
    if (!token) return;
    fetchEarnings();
  }, [token, statusFilter]);

  const fetchEarnings = async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const params: any = {};
      if (statusFilter) {
        params.status = statusFilter;
      }
      
      const res = await axios.get('/api/admin/referrals/earnings', {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err: any) {
      console.error('Error fetching admin LoveLink earnings', err);
      setError(err?.response?.data?.detail || 'Unable to load earnings. Check your admin access or try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenMarkPaid = (earning: AdminEarning) => {
    setMarkingId(earning.id);
    setPayoutNote('');
  };

  const handleCancelMarkPaid = () => {
    setMarkingId(null);
    setPayoutNote('');
  };

  const handleSubmitMarkPaid = async () => {
    if (!markingId || !token) return;
    
    try {
      setSubmitting(true);
      await axios.post(
        `/api/admin/referrals/earnings/${markingId}/mark-paid`,
        { payout_reference: payoutNote || 'Paid' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Success - refetch the list
      setMarkingId(null);
      setPayoutNote('');
      setSubmitting(false);
      await fetchEarnings();
    } catch (err: any) {
      console.error('Error marking earning as paid', err);
      const errorMsg = err?.response?.data?.detail || 'Failed to mark as paid.';
      alert(errorMsg + '\n\nNote: You cannot pay earnings still inside the 30-day hold.');
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-10">
        <p className="text-sm text-red-600">
          You must be logged in as an admin to view this page.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-purple-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => window.history.back()}
          className="inline-flex items-center text-sm font-medium text-rose-600 hover:text-rose-700 mb-4 transition-colors"
        >
          <span className="mr-1">←</span>
          Back
        </button>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
              LoveLink Earnings (Admin)
            </h1>
            <p className="mt-2 text-sm text-gray-600">
              Manage community payouts. <span className="font-semibold">Net-30 is enforced server-side</span>: only mature earnings can be marked as paid.
            </p>
          </div>
          <div className="hidden sm:block text-xs text-gray-500 text-right">
            <div>Signed in as:</div>
            <div className="font-medium text-gray-700">{user?.email || 'Unknown'}</div>
          </div>
        </div>

        {/* Filter + summary */}
        <div className="bg-white rounded-xl shadow-md p-4 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="inline-flex rounded-full bg-gray-100 p-1">
              {[
                { value: 'pending', label: 'Pending' },
                { value: 'paid', label: 'Paid' },
                { value: '', label: 'All' }
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setStatusFilter(option.value as any)}
                  className={`px-4 py-2 text-xs font-medium rounded-full flex items-center gap-1.5 transition-colors ${
                    statusFilter === option.value
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Filter className="w-3 h-3" />
                  {option.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-3 bg-emerald-50 rounded-lg px-4 py-2">
              <DollarSign className="w-5 h-5 text-emerald-600" />
              <div>
                <div className="text-xs text-emerald-700 font-medium">Mature Pending Total</div>
                <div className="text-lg font-bold text-emerald-900">
                  {data ? centsToDollars(data.total_pending_cents) : '$0.00'}
                </div>
              </div>
            </div>
          </div>
          <p className="mt-3 text-xs text-gray-500">
            💡 <span className="font-semibold">Mature pending</span> = earnings that have passed the 30-day hold and are eligible to be paid out now.
          </p>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader className="w-8 h-8 animate-spin text-emerald-600" />
          </div>
        )}

        {error && !loading && (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {!loading && !error && data && (
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">
                  Earnings Detail
                </h2>
                <div className="text-xs text-gray-500">
                  {data.earnings.length} record{data.earnings.length === 1 ? '' : 's'}
                </div>
              </div>
            </div>

            <div className="p-6">
              {data.earnings.length === 0 ? (
                <div className="text-center py-12">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                    <DollarSign className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-sm text-gray-600 mb-2">No earnings found</p>
                  <p className="text-xs text-gray-500">
                    Try switching between Pending, Paid, or All filters.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b border-gray-200">
                        <th className="py-3 pr-4 font-semibold">Date</th>
                        <th className="py-3 pr-4 font-semibold">Inviter</th>
                        <th className="py-3 pr-4 font-semibold">Invitee</th>
                        <th className="py-3 pr-4 font-semibold">Product</th>
                        <th className="py-3 pr-4 text-right font-semibold">Amount</th>
                        <th className="py-3 pr-4 font-semibold">Maturity</th>
                        <th className="py-3 pr-4 font-semibold">Status</th>
                        <th className="py-3 pl-4 text-right font-semibold">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {data.earnings.map((e) => {
                        let date: Date;
                        try {
                          date = new Date(e.created_at);
                        } catch {
                          date = new Date();
                        }
                        
                        const dateLabel = isNaN(date.getTime())
                          ? e.created_at
                          : date.toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric', 
                              year: 'numeric' 
                            });

                        const isPaid = e.status === 'paid';
                        const canPay = e.status === 'pending' && e.is_mature;

                        let maturityLabel = '';
                        let maturityClass = '';

                        if (isPaid) {
                          maturityLabel = 'Paid';
                          maturityClass = 'text-indigo-700 bg-indigo-50 border-indigo-200';
                        } else if (e.is_mature) {
                          maturityLabel = 'Mature (Net-30 ✓)';
                          maturityClass = 'text-emerald-700 bg-emerald-50 border-emerald-200';
                        } else {
                          const remaining = e.days_until_mature ?? 0;
                          maturityLabel = `In hold (${remaining}d left)`;
                          maturityClass = 'text-amber-700 bg-amber-50 border-amber-200';
                        }

                        return (
                          <tr key={e.id} className="hover:bg-gray-50 transition-colors">
                            <td className="py-3 pr-4 text-gray-700">{dateLabel}</td>
                            <td className="py-3 pr-4 text-gray-700 truncate max-w-[150px]">
                              {e.inviter_email || 'Unknown'}
                            </td>
                            <td className="py-3 pr-4 text-gray-700 truncate max-w-[150px]">
                              {e.invitee_email || 'Referred user'}
                            </td>
                            <td className="py-3 pr-4 text-gray-700">
                              {e.product_label || 'Purchase'}
                            </td>
                            <td className="py-3 pr-4 text-right font-semibold text-gray-900">
                              {centsToDollars(e.commission_amount_cents)}
                            </td>
                            <td className="py-3 pr-4">
                              <span
                                className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${maturityClass}`}
                              >
                                {maturityLabel}
                              </span>
                            </td>
                            <td className="py-3 pr-4">
                              {isPaid ? (
                                <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200">
                                  <CheckCircle2 className="w-3 h-3 mr-1" />
                                  Paid
                                </span>
                              ) : (
                                <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold text-gray-700 bg-gray-100">
                                  Pending
                                </span>
                              )}
                            </td>
                            <td className="py-3 pl-4 text-right">
                              {isPaid ? (
                                <span className="text-xs text-gray-500 italic">
                                  {e.payout_reference || 'Marked paid'}
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => handleOpenMarkPaid(e)}
                                  disabled={!canPay}
                                  className={`text-xs font-semibold rounded-full px-4 py-1.5 border transition-colors ${
                                    canPay
                                      ? 'border-emerald-600 text-emerald-700 hover:bg-emerald-50'
                                      : 'border-gray-200 text-gray-400 cursor-not-allowed'
                                  }`}
                                >
                                  {canPay ? 'Mark Paid' : 'Not Mature'}
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Mark Paid Modal */}
        {markingId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
                <h3 className="font-bold text-gray-900 text-lg">Mark Earning as Paid</h3>
              </div>
              
              <p className="text-sm text-gray-600 mb-4">
                This will move the earning from <span className="font-semibold text-emerald-700">Available</span> into{' '}
                <span className="font-semibold text-indigo-700">Paid</span>. Only do this after you have actually sent money to the
                inviter (Cash App, Zelle, bank transfer, etc.).
              </p>
              
              <label className="block text-xs font-semibold text-gray-700 mb-2">
                Payout reference (optional but recommended)
              </label>
              <input
                type="text"
                value={payoutNote}
                onChange={(e) => setPayoutNote(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                placeholder="e.g., Cash App $50 on 12/07/25"
              />
              
              <div className="mt-5 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCancelMarkPaid}
                  disabled={submitting}
                  className="text-sm px-4 py-2 rounded-full border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmitMarkPaid}
                  disabled={submitting}
                  className="inline-flex items-center gap-2 text-sm px-5 py-2 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 transition-colors"
                >
                  {submitting && <Loader className="w-4 h-4 animate-spin" />}
                  {submitting ? 'Processing...' : 'Confirm Paid'}
                </button>
              </div>
              
              <div className="mt-4 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 px-3 py-2.5 rounded-lg border border-amber-200">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <p>
                  <span className="font-semibold">Reminder:</span> Net-30 is enforced server-side. If this earning is still inside
                  the 30-day hold, the backend will reject this action.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminLoveLinkEarnings;
